import { ImageFile } from 'common/components/ImageDropzoneWithPreview/types';
import { type UserPrivilege, UserPrivilegeName, type UserRole, UserRoleName } from 'domain/UserProfile';

import type { Attachment } from 'attachment/api/types';

export enum ProxyMode {
  Automatic = 'AUTOMATIC',
  NoProxy = 'NO_PROXY',
  TcpProxy = 'TCP_PROXY',
  UdpProxy = 'UDP_PROXY',
}

export interface CommunityGuest {
  email: string;
  communityRole: UserRoleName;
  privileges: UserPrivilegeName[];
}

export interface CommunitySettings {
  membersLimit: number;
  eventsPerMonthLimit: number;
  usersPerEventLimit: number;
  recurringNattersPerMonthLimit: number;
  usersPerRecurringNattersLimit: number;
  /** @deprecated Read-only — backend does not persist updates to this field */
  transcriptionEnabled: boolean;
  microsoftSSOEnabled: boolean;
  googleSSOEnabled: boolean;
  companySSOEnabled: boolean;
  proxyMode: ProxyMode;
  mfaEnforced?: boolean;
  automaticOptOutEnabled: boolean;
  openNatterEnabled: boolean;
  natalieEnabled: boolean;
  anonymityFilterEnabled: boolean;
  roleFieldDisabled: boolean;
  magicLinkAuthEnabled?: boolean | null;
}

export interface CommunityApiPayload {
  id: number;
  hash: string;
  name: string;
  logoImageLink: string | null;
  logoImageLinkSmall: string | null;
  /** @deprecated */
  brandingColor: string | null;
  ownerId: number;
  communityMembersCount: number;
  communitySettings: CommunitySettings;
  defaultEventImage?: Attachment | null;
  slug?: string;
}

export interface Community {
  id: number;
  hash: string;
  name: string;
  logoImageLink: string | undefined;
  logoImageLinkSmall: string | undefined;
  /** @deprecated */
  brandingColor: string | undefined;
  ownerId: number;
  communityMembersCount: number;
  communitySettings?: CommunitySettings;
  defaultEventImage?: Attachment | null;
  slug?: string;
}

export interface Guest {
  accountId: number;
  id: number;
  name: string;
  email: string;
  displayName: string;
  avatarLink: string;
  role: string | null;
}

export interface CommunityMemberFormData {
  avatar: { data: string; path: string };
  socials: Partial<CommunityMemberSocials>;
  role: string;
  communityRole: UserRoleName;
  privileges: UserPrivilegeName[];
}

export interface CommunityMemberApiPayload {
  socials?: Partial<CommunityMemberSocials>;
  communityRole: UserRoleName;
  hasCronofyCredentials: boolean;
  accountId: number;
  id: number;
  name: string | null;
  email: string | null;
  displayName: string | null;
  avatarLink: string | null;
  role: string | null;
  privileges?: UserPrivilege[];
  isOrganizationMember?: boolean;
}

export interface CommunityMember extends Guest {
  socials?: Partial<CommunityMemberSocials>;
  communityRole: UserRoleName;
  hasCronofyCredentials: boolean;
  privileges: UserPrivilege[];
  isOrganizationMember: boolean;
}

export interface CommunityMemberSocials {
  facebook?: string | null;
  instagram?: string | null;
  linkedin?: string | null;
  twitter?: string | null;
}

export interface CommunityTag {
  id: number;
  name: string;
  category?: string;
}

/** @deprecated  TODO: Infer type from community form schema */
export interface CommunityFormValues {
  id?: number;
  hash?: string;
  name: string;
  color: { hex: string };
  brandingColor?: string;
  logo: ImageFile;
  email: string;
  guests: CommunityGuest[];
  communitySettings: CommunitySettings;
}

export interface CreateCommunityRequestBody {
  /** @deprecated */
  brandingColor?: string;
  name: string;
  communitySettings?: CommunitySettings;
}

export interface UpdateCommunityRequestBody extends CreateCommunityRequestBody {
  logoImageLink?: string | null;
  defaultEventImageAttachmentId?: string | null;
}

/** @deprecated Please use **UpdateCommunityRequest** from `src/store/apis/community/types.ts` */
export interface UpdateCommunityData {
  id: number;
  logo: ImageFile | ImageData;
  logoImageLink?: string;
  brandingColor?: string;
  name: string;
  guests: CommunityGuest[];
  defaultEventImageAttachmentId?: string | null;
}

export interface CommunityAdminToggleRequest {
  accountId: number;
  displayName: string;
  isPromote: boolean;
  communityId: number;
}

export interface CommunityRolesPrivileges {
  role: UserRole;
  privileges: UserPrivilege[];
}
